import { useEffect, useState, useRef } from "react";
import { MapPin, X, ChevronDown } from "lucide-react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";

/**
 * Reusable Destination + City filter.
 *
 * - FOUNDER / BDOM: interactive dropdown to slice any operational list by
 *   country + city. Emits `{destination, city}` to the parent via `onChange`.
 * - Everyone else with a set profile location: static "you're viewing X"
 *   badge (they can't override — the backend already auto-scopes them).
 *
 * Parent pages pass the current filter as `value` and re-fetch on change.
 */
export default function DestCityFilter({ value, onChange, className = "" }) {
  const { user } = useAuth();
  const role = user?.effective_role || "";
  const isAdmin = role === "FOUNDER" || role === "BDOM";
  const [countries, setCountries] = useState([]);
  const [open, setOpen] = useState(false);
  const [dest, setDest] = useState(value?.destination || "");
  const [city, setCity] = useState(value?.city || "");
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/accommodations/countries")
      .then(({ data }) => setCountries(data?.countries || []))
      .catch(() => {});
  }, [isAdmin]);

  // Sync when the parent externally resets the filter (e.g. tab switch).
  useEffect(() => {
    setDest(value?.destination || "");
    setCity(value?.city || "");
  }, [value?.destination, value?.city]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!isAdmin) {
    const userDest = user?.destination || "";
    const userCity = user?.city || "";
    if (!userDest && !userCity) return null;
    const label = userCity ? `${userCity}, ${userDest}` : userDest;
    return (
      <div
        data-testid="destcity-badge"
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[color:var(--aii-green-tint)] border border-[color:var(--aii-border)] text-xs ${className}`}
        title="Backend auto-scopes lists to your assigned location"
      >
        <MapPin size={12} className="text-brand" />
        <span className="font-semibold text-[color:var(--aii-text-1)]">{label}</span>
        <span className="text-[color:var(--aii-muted)] hidden sm:inline">— your office</span>
      </div>
    );
  }

  const apply = () => {
    onChange?.({ destination: dest.trim(), city: city.trim() });
    setOpen(false);
  };

  const clear = () => {
    setDest(""); setCity("");
    onChange?.({ destination: "", city: "" });
    setOpen(false);
  };

  const activeDest = value?.destination || "";
  const activeCity = value?.city || "";
  const isActive = Boolean(activeDest || activeCity);
  const label = isActive
    ? `Viewing: ${[activeCity, activeDest].filter(Boolean).join(", ")}`
    : "All destinations";

  return (
    <div ref={rootRef} className={`relative inline-block ${className}`}>
      <button
        data-testid="destcity-filter-btn"
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-semibold transition ${
          isActive
            ? "bg-[color:var(--aii-green-tint)] border-brand text-brand"
            : "border-[color:var(--aii-border)] bg-white text-[color:var(--aii-text-1)] hover:bg-[color:var(--aii-green-tint)]"
        }`}
      >
        <MapPin size={12} />
        <span className="max-w-[220px] truncate">{label}</span>
        <ChevronDown size={12} className={`transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid="destcity-filter-menu"
          className="absolute right-0 top-full mt-2 w-72 aii-card p-3 z-40 shadow-xl"
        >
          <div className="text-[10px] uppercase tracking-widest text-brand font-bold mb-2 flex items-center gap-1">
            <MapPin size={11} /> Filter by office
          </div>
          <label className="text-[10px] font-semibold text-[color:var(--aii-text-2)] uppercase tracking-wider">Destination (country)</label>
          <select
            data-testid="destcity-filter-dest"
            value={dest}
            onChange={(e) => setDest(e.target.value)}
            className="mt-1 mb-3 w-full px-2 py-1.5 border border-[color:var(--aii-border)] rounded bg-white text-sm"
          >
            <option value="">— any —</option>
            {countries.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <label className="text-[10px] font-semibold text-[color:var(--aii-text-2)] uppercase tracking-wider">City</label>
          <input
            data-testid="destcity-filter-city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="e.g. Jaipur (blank = any)"
            className="mt-1 mb-3 w-full px-2 py-1.5 border border-[color:var(--aii-border)] rounded bg-white text-sm"
          />
          <div className="flex justify-between gap-2">
            <button
              data-testid="destcity-filter-clear"
              type="button"
              onClick={clear}
              className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded hover:bg-slate-50 flex items-center gap-1"
            >
              <X size={11} /> Clear
            </button>
            <button
              data-testid="destcity-filter-apply"
              type="button"
              onClick={apply}
              className="text-xs px-3 py-1.5 bg-brand text-white font-semibold rounded hover:bg-brand-dark"
            >
              Apply
            </button>
          </div>
          <p className="text-[10px] text-[color:var(--aii-muted)] mt-2 leading-snug">
            Non-Founder users are always scoped to their assigned location — this filter is Founder/BDOM-only.
          </p>
        </div>
      )}
    </div>
  );
}
